import { takeLatest } from 'redux-saga/effects';
import {
  EDIT_PAR_AM_BOOKING,
  ADD_PAR_AM_BOOKING,
  ADD_BOOKING_V3,
  EDIT_BOOKING_V3,
  GET_AVAILABLE_TIME,
  GET_AMENITY_AVAILABILITY_V2,
  GET_BOOKING_COST_BREAKDOWN,
  CANCEL_BOOKING,
  CANCEL_BOOKING_V3,
  CONFIRM_BOOKING_V3,
  GET_PARKING_PAR_AM_BOOKINGS,
  GET_SELECTED_BOOKINGS,
  GET_BOOKINGS_LIST_V3,
  GET_BOOKINGS_CALENDAR_V3,
  GET_BOOKING_V3,
  GET_AMENITY_PAR_AM_BOOKINGS,
  GET_PAR_AM_BOOKING,
  REMOVE_BOOKING,
  SEARCH_BOOK_PARKINGS,
  SEARCH_BOOK_AMENITIES,
  GET_ELEVATOR_PAR_AM_BOOKINGS,
  SEARCH_BOOK_ELEVATOR,
  SEARCH_BOOK_BY_TYPE,
  SEARCH_BOOK_BY_DATE_RANGE
} from '../constants';
import apiCall from '../../utils/apiCall';
import { appendStatusQuery } from '../../views/bookings/V2/bookingsFilterHelpers';

function normalizeAmenityIds(amenityIds) {
  if (!Array.isArray(amenityIds)) return [];
  return amenityIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0);
}

function appendAmenityIdQuery(baseQuery, amenityIds) {
  const ids = normalizeAmenityIds(amenityIds);
  if (ids.length === 0) return baseQuery;
  const separator = baseQuery ? '&' : '';
  return `${baseQuery}${separator}amenity_id=${ids.join(',')}`;
}

const getSelectedBookings = apiCall({
  type: GET_SELECTED_BOOKINGS,
  method: 'GET',
  path: ({ id, page, amenityId, orgId, perPage = 20, startDate, endDate }) => {
    let url = `/bookings?building=${id}&page=${page}&type=${amenityId}&per_page=${perPage}`;
    if (orgId) {
      url += `&org=${orgId}`;
    }
    if (startDate) {
      url += `&start_date=${startDate}`;
    }
    if (endDate) {
      url += `&end_date=${endDate}`;
    }
    return url;
  }
});

const getBookingsListV3 = apiCall({
  type: GET_BOOKINGS_LIST_V3,
  method: 'GET',
  path: ({
    id,
    page = 1,
    amenityIds = [],
    orgId,
    perPage = 20,
    startDate,
    endDate,
    typeId,
    search,
    statusIds = []
  }) => {
    const q = new URLSearchParams();
    q.set('view', 'list');
    q.set('building', String(id));
    q.set('page', String(page));
    q.set('per_page', String(perPage));
    if (startDate) {
      q.set('start_date', String(startDate));
    }
    if (endDate) {
      q.set('end_date', String(endDate));
    }
    if (orgId) {
      q.set('org', String(orgId));
    }
    if (typeId !== undefined && typeId !== null) {
      q.set('type_id', String(typeId));
    }
    if (search?.trim()) {
      q.set('search', search.trim());
    }
    let query = appendAmenityIdQuery(q.toString(), amenityIds);
    query = appendStatusQuery(query, statusIds);
    return `/v3/bookings?${query}`;
  }
});

const getBookingsCalendarV3 = apiCall({
  type: GET_BOOKINGS_CALENDAR_V3,
  method: 'GET',
  path: ({
    building,
    amenityIds = [],
    statusIds = [],
    orgId,
    granularity = 'week',
    date
  }) => {
    const q = new URLSearchParams();
    q.set('view', 'calendar');
    q.set('calendar', granularity);
    q.set('date', date);
    q.set('building', String(building));
    if (orgId) q.set('org', String(orgId));
    let query = appendAmenityIdQuery(q.toString(), amenityIds);
    query = appendStatusQuery(query, statusIds);
    return `/v3/bookings?${query}`;
  }
});

const editParAmBookings = apiCall({
  type: EDIT_PAR_AM_BOOKING,
  method: 'PUT',
  path: ({ id }) => `/bookings/${id}`
});

const cancelBooking = apiCall({
  type: CANCEL_BOOKING,
  method: 'post',
  path: ({ id }) => `/bookings/${id}/cancel`
});

const cancelBookingV3 = apiCall({
  type: CANCEL_BOOKING_V3,
  method: 'post',
  path: ({ id }) => `/v3/bookings/${id}/cancel`
});

const confirmBookingV3 = apiCall({
  type: CONFIRM_BOOKING_V3,
  method: 'post',
  path: ({ id }) => `/v3/bookings/${id}/confirm`
});

const addParAmBookings = apiCall({
  type: ADD_PAR_AM_BOOKING,
  method: 'POST',
  path: '/bookings'
});

const addBookingV3 = apiCall({
  type: ADD_BOOKING_V3,
  method: 'POST',
  path: '/v3/bookings'
});

const editBookingV3 = apiCall({
  type: EDIT_BOOKING_V3,
  method: 'PUT',
  path: ({ id }) => `/v3/bookings/${id}`
});

const getAmenityAvailabilityV2 = apiCall({
  type: GET_AMENITY_AVAILABILITY_V2,
  method: 'get',
  path: ({ amenity_id }) => `/v2/amenities/${amenity_id}/available`
});

const getBookingCostBreakdown = apiCall({
  type: GET_BOOKING_COST_BREAKDOWN,
  method: 'get',
  path: () => '/booking/cost-breakdown'
});

const getAvailableTime = apiCall({
  type: GET_AVAILABLE_TIME,
  method: 'get',
  path: ({ amenity_id, month, year, day }) =>
    `/amenities/${amenity_id}/available?month=${month}&year=${year}&day=${day}`
});

const getParAMBooking = apiCall({
  type: GET_PAR_AM_BOOKING,
  method: 'get',
  path: ({ id }) => `/bookings/${id}`
});

const getBookingV3 = apiCall({
  type: GET_BOOKING_V3,
  method: 'get',
  path: ({ id }) => `/v3/bookings/${id}`
});

const removeParAmBooking = apiCall({
  type: REMOVE_BOOKING,
  method: 'delete',
  path: ({ id }) => `/bookings/${id}`
});

const searchBookedByType = apiCall({
  type: SEARCH_BOOK_BY_TYPE,
  method: 'get',
  newUrl: true,
  path: ({
    query,
    amenityId,
    page = 1,
    perpage = 20,
    search_col = null,
    building = 0,
    org = 0
  }) => {
    let url;
    amenityId > 0
      ? (url = `/search?entity=booking&type=${amenityId}&q=${query}&page=${page}&per_page=${perpage}`)
      : (url = `/search?entity=booking&q=${query}&page=${page}&per_page=${perpage}`);

    if (building > 0) {
      url += `&building=${building}`;
    }
    if (!building && org > 0) {
      url += `&org=${org}`;
    }
    if (search_col) {
      url += `&search_col=${search_col}`;
    }

    return url;
  }
});

const searchBookedByDateRange = apiCall({
  type: SEARCH_BOOK_BY_DATE_RANGE,
  method: 'get',
  newUrl: true,
  path: ({ building = 0, org = 0, startDate = null, endDate = null }) => {
    let url = `/search?entity=booking&start_date=${startDate}&end_date=${endDate}`;

    if (building > 0) {
      url += `&building=${building}`;
    }
    if (!building && org > 0) {
      url += `&org=${org}`;
    }

    return url;
  }
});

// not used
const searchBookedAmenities = apiCall({
  type: SEARCH_BOOK_AMENITIES,
  method: 'get',
  newUrl: true,
  path: ({ query, page = 1, perpage = 100, buildingId = 0 }) =>
    `/search?entity=booking&type=1&q=${query}&page=${page}&per_page=${perpage}&building=${buildingId}`
});

// the component of which this is called is not been used
const searchBookedParkings = apiCall({
  type: SEARCH_BOOK_PARKINGS,
  method: 'get',
  newUrl: true,
  path: ({ query, page = 1, perpage = 100, building = 0 }) =>
    `/search?entity=booking&type=3&q=${query}&page=${page}&per_page=${perpage}&building=${building}`
});

// not used
const searchBookedElevators = apiCall({
  type: SEARCH_BOOK_ELEVATOR,
  method: 'get',
  newUrl: true,
  path: ({ query, page = 1, perpage = 100, building = 0 }) =>
    `/search?entity=booking&type=2&q=${query}&page=${page}&per_page=${perpage}&building=${building}`
});

export default function* amenitiesSaga() {
  yield takeLatest(SEARCH_BOOK_AMENITIES, searchBookedAmenities);
  yield takeLatest(SEARCH_BOOK_PARKINGS, searchBookedParkings);
  yield takeLatest(SEARCH_BOOK_ELEVATOR, searchBookedElevators);
  yield takeLatest(REMOVE_BOOKING, removeParAmBooking);
  yield takeLatest(GET_SELECTED_BOOKINGS, getSelectedBookings);
  yield takeLatest(GET_BOOKINGS_LIST_V3, getBookingsListV3);
  yield takeLatest(EDIT_PAR_AM_BOOKING, editParAmBookings);
  yield takeLatest(ADD_PAR_AM_BOOKING, addParAmBookings);
  yield takeLatest(ADD_BOOKING_V3, addBookingV3);
  yield takeLatest(EDIT_BOOKING_V3, editBookingV3);
  yield takeLatest(GET_AMENITY_AVAILABILITY_V2, getAmenityAvailabilityV2);
  yield takeLatest(GET_BOOKING_COST_BREAKDOWN, getBookingCostBreakdown);
  yield takeLatest(GET_AVAILABLE_TIME, getAvailableTime);
  yield takeLatest(CANCEL_BOOKING, cancelBooking);
  yield takeLatest(CANCEL_BOOKING_V3, cancelBookingV3);
  yield takeLatest(CONFIRM_BOOKING_V3, confirmBookingV3);
  yield takeLatest(GET_PAR_AM_BOOKING, getParAMBooking);
  yield takeLatest(GET_BOOKING_V3, getBookingV3);
  yield takeLatest(SEARCH_BOOK_BY_TYPE, searchBookedByType);
  yield takeLatest(SEARCH_BOOK_BY_DATE_RANGE, searchBookedByDateRange);
  yield takeLatest(GET_BOOKINGS_CALENDAR_V3, getBookingsCalendarV3);
}
